/**
 * 遊び方の記録を、ブラウザ・アプリの中で動かすための組み立て。
 * 中身（ためる・送る・上限）は telemetry.ts、形の検査は telemetrySchema.ts。
 */

import { metaStorageKey, settingsStorageKey } from '../game/config/gameConfig';
import { apiUrl, nativeOS } from '../platform/native';
import { RankingIdentityStore } from './dailyRanking';
import { Telemetry, flushTelemetry, getTelemetry, setTelemetry, track } from './telemetry';
import { TELEMETRY_MAX_BODY_BYTES, jstDay, type TelemetryBatch } from './telemetrySchema';

declare const __APP_VERSION__: string;

export function appVersion(): string {
  return typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'dev';
}

/** 保存されている設定から「送る」を読む（県えらびの画面には設定の部品がないため）。既定は入。 */
export function usageStatsEnabledInStorage(): boolean {
  try {
    const raw = localStorage.getItem(settingsStorageKey());
    if (!raw) return true;
    const parsed = JSON.parse(raw) as { sendUsageStats?: unknown };
    return parsed.sendUsageStats !== false;
  } catch {
    return true;
  }
}

/** 送る。画面が閉じかけていても届くよう keepalive を付ける。失敗は false（例外は投げない）。 */
async function sendBatch(batch: TelemetryBatch): Promise<boolean> {
  try {
    const body = JSON.stringify(batch);
    if (body.length > TELEMETRY_MAX_BODY_BYTES) return false;
    const res = await fetch(apiUrl('/api/events'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit',
    });
    // 400（形がおかしい）は何度送っても同じなので、送れたことにして捨てる
    return res.ok || res.status === 400;
  } catch {
    return false;
  }
}

const LAUNCH_FLAG = 'meshi-merge:telemetry-launched';

/** 計測の detail に入れられる形に整える（英小文字・数字・_ . - だけ、32文字まで）。 */
function cleanUtm(value: string | null): string {
  return (value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, '')
    .slice(0, 32);
}

/**
 * Web 版の流入元：URL の utm_source / utm_medium を launch の detail にする（例: share.x）。
 * URL から読むだけで、どこにも保存しない。付いていなければ undefined。
 */
export function launchDetailFromSearch(search: string): string | undefined {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return undefined;
  }
  const source = cleanUtm(params.get('utm_source'));
  if (!source) return undefined;
  const medium = cleanUtm(params.get('utm_medium'));
  return (medium ? `${source}.${medium}` : source).slice(0, 40);
}

/**
 * 前面に戻ったとき、launch を送るか。
 * iOS は閉じずに翌日また開く人が多い。最後に launch を送った日（日本時間）と今日が違えば送る。
 */
export function shouldLaunchOnResume(lastLaunchDay: string, now: number): boolean {
  return lastLaunchDay !== jstDay(now);
}

/**
 * 計測を始める（ページごとに1回）。
 * @param enabled 設定の「送る」。省略時は保存されている設定を読む
 * @param region  県の画面なら、その県（起動の記録に添える）
 */
export function startTelemetry(options: { enabled?: () => boolean; region?: string } = {}): Telemetry {
  const existing = getTelemetry();
  if (existing) return existing;
  let ids: RankingIdentityStore | null = null;
  const os = nativeOS();
  const telemetry = new Telemetry({
    storageKey: metaStorageKey('telemetry'),
    // 端末ID はランキングの player ID を使う（無ければここで作られる）
    device: () => (ids ??= new RankingIdentityStore()).identity.player,
    platform: os ?? 'web',
    app: appVersion(),
    enabled: options.enabled ?? usageStatsEnabledInStorage,
    send: sendBatch,
  });
  setTelemetry(telemetry);
  const region = options.region ? { region: options.region } : {};

  // 起動：同じタブ（アプリ）の中で県えらび → 県へ移っても1回だけ
  let launched = false;
  try {
    launched = sessionStorage.getItem(LAUNCH_FLAG) === '1';
    sessionStorage.setItem(LAUNCH_FLAG, '1');
  } catch {
    /* 数えすぎるほうを選ぶ */
  }
  if (!launched) {
    // Web 版だけ、共有リンクなどの utm を添える（アプリは URL から開かれない）
    const detail = os ? undefined : launchDetailFromSearch(location.search);
    track('launch', detail ? { ...region, detail } : region);
    telemetry.noteLaunchDay(jstDay(Date.now()));
  } else if (!telemetry.lastLaunchDay) {
    telemetry.noteLaunchDay(jstDay(Date.now()));
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      // 画面が隠れたとき（ホームに戻る・タブを閉じる）にまとめて送る
      flushTelemetry();
      return;
    }
    // 前面に戻った：日が変わっていれば、その日の起動として数える（iOS の復帰）
    const now = Date.now();
    if (shouldLaunchOnResume(telemetry.lastLaunchDay, now)) {
      track('launch', { ...region, detail: 'resume' });
      telemetry.noteLaunchDay(jstDay(now));
    }
  });
  window.addEventListener('pagehide', () => flushTelemetry());
  // 前回送れなかった分と起動の記録は、少し待ってから（最初の表示の邪魔をしない）
  window.setTimeout(() => flushTelemetry(), 8000);
  return telemetry;
}
